import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Disc3, Loader2, Pause, Pencil, Play, Plus, Trash2 } from "lucide-react";
import Layout from "@/components/Layout";
import { Pill } from "@/components/Music";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTenant } from "@/context/TenantContext";
import { api, apiError } from "@/lib/api";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

export default function Workspaces() {
  const { tenants, tenantId, setTenantId, refreshTenants } = useTenant();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (params.get("new")) {
      setOpen(true);
      setParams({});
    }
  }, [params, setParams]);
  const [form, setForm] = useState({ name: "", publisher_pro: "BMI", publisher_ipi: "", admin_name: "", admin_email: "", admin_password: "" });
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.admin_email) {
        delete body.admin_email;
        delete body.admin_password;
        delete body.admin_name;
      }
      const { data } = await api.post("/tenants", body);
      await refreshTenants();
      setOpen(false);
      toast.success(`${data.tenant.name} created`);
      setTenantId(data.tenant.tenant_id);
      navigate("/import");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (t) => {
    const name = window.prompt("New name", t.name);
    if (!name || name === t.name) return;
    try {
      await api.put(`/tenants/${t.tenant_id}`, { name });
      refreshTenants();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const toggleActive = async (t) => {
    try {
      await api.put(`/tenants/${t.tenant_id}`, { active: !t.active });
      refreshTenants();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const remove = async (t) => {
    const typed = window.prompt(`This deletes every track, writer and login in ${t.name}. Type the name to confirm:`);
    if (typed == null) return;
    try {
      await api.delete(`/tenants/${t.tenant_id}`, { params: { confirm: typed } });
      toast.success("Deleted");
      const list = await refreshTenants();
      if (t.tenant_id === tenantId) setTenantId(list[0]?.tenant_id || null);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      owner
      title="Accounts"
      subtitle="One workspace per publishing company — each has its own catalog, writers and team"
      action={
        <Button className="bg-brand hover:bg-brand-hover" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">New workspace</span>
        </Button>
      }
    >
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {tenants.map((t) => (
          <article key={t.tenant_id} className={`panel p-5 space-y-4 ${t.tenant_id === tenantId ? "ring-2 ring-brand-ring" : ""} ${t.active === false ? "opacity-70" : ""}`}>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-brand flex items-center justify-center">
                <Disc3 className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-[17px] font-semibold truncate">{t.name}</div>
                <div className="font-mono text-[11px] text-muted2">
                  {t.publisher_pro}
                  {t.publisher_ipi ? ` · IPI ${t.publisher_ipi}` : ""}
                </div>
              </div>
              {t.tenant_id === tenantId && <Pill tone="brand">Open</Pill>}
              {t.active === false && <Pill tone="warn">Suspended</Pill>}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                ["Tracks", t.track_count],
                ["Writers", t.writer_count],
                ["Logins", t.user_count],
              ].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-surface py-2">
                  <div className="font-display text-[20px] font-semibold">{v}</div>
                  <div className="eyebrow text-[9.5px]">{l}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1 pt-3 border-t border-line">
              <button onClick={() => rename(t)} aria-label="Rename" title="Rename" className="p-1.5 rounded-lg text-muted2 hover:bg-brand-light hover:text-brand">
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => toggleActive(t)} aria-label={t.active === false ? "Reactivate" : "Suspend"} title={t.active === false ? "Reactivate" : "Suspend logins"} className="p-1.5 rounded-lg text-muted2 hover:bg-brand-light hover:text-brand">
                {t.active === false ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(t)} aria-label="Delete" title="Delete" className="p-1.5 rounded-lg text-muted2 hover:bg-rose-50 hover:text-stock-out">
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setTenantId(t.tenant_id);
                  navigate("/overview");
                }}
              >
                Open <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </article>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New workspace</DialogTitle>
          </DialogHeader>
          <form id="new-ws" onSubmit={create} className="space-y-3">
            <input className={inputCls} required placeholder="Publishing company name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <div className="grid grid-cols-[1fr_1.5fr] gap-2">
              <select className={inputCls} value={form.publisher_pro} onChange={(e) => setForm({ ...form, publisher_pro: e.target.value })} aria-label="PRO">
                {["BMI", "ASCAP", "SESAC", "SOCAN", "JACAP", "PRS"].map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
              <input className={`${inputCls} font-mono`} placeholder="Publisher IPI (optional)" value={form.publisher_ipi} onChange={(e) => setForm({ ...form, publisher_ipi: e.target.value })} />
            </div>
            <div className="pt-2 text-[12.5px] text-muted2">Optional — an admin who runs this workspace:</div>
            <input className={inputCls} placeholder="Admin name" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} />
            <input className={inputCls} type="email" placeholder="Admin email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
            {form.admin_email && <input className={inputCls} required minLength={8} placeholder="Temporary password (8+)" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} />}
          </form>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="new-ws" disabled={busy} className="bg-brand hover:bg-brand-hover">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
